import { splitFilterValues } from "@nymbl/nos2-rules";
import { z } from "zod";
import { JOB_RUN_STATUSES } from "@/lib/enums";
import { JOB_MODES, JOB_NAMES } from "@/lib/jobs/schedule";

/** Settings, Jobs: one job, as `GET /api/admin/jobs` answers it. */
const jobRunSummarySchema = z.object({
  id: z.string(),
  job: z.string(),
  slot: z.string(),
  status: z.enum(JOB_RUN_STATUSES),
  attempts: z.number(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  counts: z.record(z.string(), z.number()).nullable(),
  error: z.string().nullable(),
});
export type JobRunRow = z.infer<typeof jobRunSummarySchema>;

const jobViewSchema = z.object({
  job: z.enum(JOB_NAMES),
  label: z.string(),
  schedule: z.string(),
  mode: z.enum(JOB_MODES),
  lastRun: jobRunSummarySchema.nullable(),
  nextRun: z.string().nullable(),
});
export type JobView = z.infer<typeof jobViewSchema>;

export const jobsResponseSchema = z.object({ data: z.array(jobViewSchema) });

export const jobRunsPageSchema = z.object({
  data: z.array(jobRunSummarySchema),
  nextCursor: z.string().nullable(),
  prevCursor: z.string().nullable(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
  total: z.number(),
});

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A `from..to` date filter, either side open; anything unreadable is no filter. */
const dayRange = z
  .string()
  .optional()
  .transform((raw) => {
    if (!raw) return undefined;
    const [from = "", to = ""] = raw.includes("..") ? raw.split("..") : [raw, raw];
    const day = (v: string) => (DAY.test(v) && !Number.isNaN(Date.parse(v)) ? v : null);
    const range = { from: day(from), to: day(to) };
    return range.from || range.to ? range : undefined;
  });

/** A `min..max` number filter, either side open. */
const numberRange = z
  .string()
  .optional()
  .transform((raw) => {
    if (!raw) return undefined;
    const [min = "", max = ""] = raw.includes("..") ? raw.split("..") : [raw, raw];
    const num = (v: string) => (v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
    const range = { min: num(min), max: num(max) };
    return range.min !== null || range.max !== null ? range : undefined;
  });

/** Comma-joined values (`joinFilterValues`), kept only when they are known values. */
const valuesOf = <T extends string>(known: readonly T[]) =>
  z
    .string()
    .optional()
    .transform((raw) => {
      const values = splitFilterValues(raw ?? "").filter((v): v is T =>
        (known as readonly string[]).includes(v),
      );
      return values.length > 0 ? values : undefined;
    });

/**
 * The run history's filters, off the query string (`buildListParams`: `search`, `status` and
 * `f.<column>`). Unknown values drop to no filter, so a stale link never 400s the page.
 */
export const jobRunsQuerySchema = z.object({
  search: z.string().trim().optional(),
  job: valuesOf(JOB_NAMES),
  status: valuesOf(JOB_RUN_STATUSES),
  slot: dayRange,
  startedAt: dayRange,
  finishedAt: dayRange,
  attempts: numberRange,
});
export type JobRunsQuery = z.infer<typeof jobRunsQuerySchema>;

/** Settings, Jobs' address: `?view=history` is the run history tab; anything else is Jobs. */
export const jobsSearchSchema = z.object({
  view: z.literal("history").optional().catch(undefined),
});
